import {
	ExchangeTargetFlightDecision,
	ExchangeTargetFlightDirectionWithLegs,
	ExchangeTargetSelectedFlight
} from '../model/types';

/**
 * [WEBSKY-8382, WEBSKY-8386, WEBSKY-8630]:
 * Индекс первого плеча: для `CreateExchange.segmentGroupId` и для API при true RT-комбо (одинаковый combo-id).
 */
export const CREATE_EXCHANGE_LEG_INDEX = 0;

/**
 * [WEBSKY-8382, WEBSKY-8386]:
 * Собирает id рейсов обратного плеча, которые можно комбинировать с выбранным тарифом первого плеча.
 *
 * Если `priceId` первого плеча не входит в этот список, для API берётся последний выбранный рейс.
 *
 * @param flightDirections — направления из ответа `GetExchangeVariants`
 * @param fareGroupId — `groupId` рейса первого плеча
 * @param currentLegIndex — индекс текущего плеча (обычно {@link CREATE_EXCHANGE_LEG_INDEX})
 * @returns id рейсов на следующем направлении с тем же тарифом
 */
export const collectReturnLegFlightIds = (
	flightDirections: ExchangeTargetFlightDirectionWithLegs[],
	fareGroupId: string,
	currentLegIndex: number
): Set<string> => {
	const returnDirection = flightDirections[currentLegIndex + 1];
	const ids = new Set<string>();

	if (!returnDirection?.legs) {
		return ids;
	}

	returnDirection.legs.forEach(leg => {
		leg.pricesForFareGroups?.forEach(group => {
			if (group.fareFamily?.id !== fareGroupId) {
				return;
			}

			group.prices?.forEach(price => {
				if (price.flight?.id) {
					ids.add(price.flight.id);
				}
			});
		});
	});

	return ids;
};

/**
 * [WEBSKY-8382, WEBSKY-8386, WEBSKY-8630]:
 * Выбирает рейс, по которому уходят `ExchangePrice` и `CreateExchange`, и возвращает причину выбора.
 *
 * Правила:
 * - выбор не завершён → индекс не определён;
 * - один сегмент (OW) → первый рейс;
 * - RT, первый рейс не входит в комбо → последний выбранный рейс;
 * - RT, первый рейс входит в комбо и `priceId` плеч совпадают → первый рейс;
 * - RT, первый рейс входит в комбо, но `priceId` плеч разные → последний выбранный рейс.
 *
 * @param selectedFlights — выбранные рейсы в мастере обмена
 * @param segmentsCount — сколько сегментов обменивают
 * @param flightDirections — направления из ответа `GetExchangeVariants`
 */
export const resolveExchangeTargetFlightDecision = (
	selectedFlights: ExchangeTargetSelectedFlight[],
	segmentsCount: number,
	flightDirections: ExchangeTargetFlightDirectionWithLegs[]
): ExchangeTargetFlightDecision => {
	const firstPriceId = selectedFlights[0]?.priceId ?? null;

	if (selectedFlights.length === 0 || selectedFlights.length < segmentsCount) {
		return {
			targetFlightIndex: undefined,
			reason: 'selectionIncomplete',
			isFirstFlightInReturnCombo: null,
			returnLegFlightIds: [],
			firstPriceId
		};
	}

	if (selectedFlights.length === 1) {
		return {
			targetFlightIndex: 0,
			reason: 'owSingleFlight',
			isFirstFlightInReturnCombo: null,
			returnLegFlightIds: [],
			firstPriceId
		};
	}

	const firstFlight = selectedFlights[0];
	const lastFlight = selectedFlights[selectedFlights.length - 1];
	const returnLegFlightIds = collectReturnLegFlightIds(
		flightDirections,
		firstFlight.groupId ?? '',
		CREATE_EXCHANGE_LEG_INDEX
	);
	const returnLegFlightIdsList = [...returnLegFlightIds];
	const isFirstFlightInReturnCombo = returnLegFlightIds.has(firstFlight.priceId);

	if (!isFirstFlightInReturnCombo) {
		return {
			targetFlightIndex: selectedFlights.length - 1,
			reason: 'firstNotInReturnCombo',
			isFirstFlightInReturnCombo,
			returnLegFlightIds: returnLegFlightIdsList,
			firstPriceId
		};
	}

	if (firstFlight.priceId !== lastFlight.priceId) {
		return {
			targetFlightIndex: selectedFlights.length - 1,
			reason: 'rtComboPriceMismatch',
			isFirstFlightInReturnCombo,
			returnLegFlightIds: returnLegFlightIdsList,
			firstPriceId
		};
	}

	return {
		targetFlightIndex: CREATE_EXCHANGE_LEG_INDEX,
		reason: 'firstInReturnCombo',
		isFirstFlightInReturnCombo,
		returnLegFlightIds: returnLegFlightIdsList,
		firstPriceId
	};
};

/**
 * [WEBSKY-8382, WEBSKY-8386, WEBSKY-8630]:
 * Возвращает индекс рейса в `selectedFlights` для параметров API обмена.
 *
 * Обёртка над {@link resolveExchangeTargetFlightDecision}. `undefined` — выбор ещё не завершён.
 *
 * @param selectedFlights — выбранные рейсы в мастере обмена
 * @param segmentsCount — сколько сегментов обменивают
 * @param flightDirections — направления из ответа `GetExchangeVariants`
 * @returns индекс рейса для API или `undefined`, если выбор не завершён
 */
export const resolveExchangeTargetFlightIndex = (
	selectedFlights: ExchangeTargetSelectedFlight[],
	segmentsCount: number,
	flightDirections: ExchangeTargetFlightDirectionWithLegs[]
): number | undefined =>
	resolveExchangeTargetFlightDecision(selectedFlights, segmentsCount, flightDirections).targetFlightIndex;
